/*
 * Copyright (c) 2024
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: AI Assistant
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Component, OnInit, OnDestroy, ViewChild, ElementRef} from '@angular/core';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {Subject} from 'rxjs';
import {ClientUserProvider, ReportProvider} from '../../../../../providers';
import {ClientUser, Report, ReportSiteVisitResult, User, Helpers, ReportClientType, ReportClientUser} from '../../../../../model';
import {ReportService} from '../../../../../services/report.service';
import {StageService, UserService, ModeService, LogService, CalculationService} from '../../../../../services';
import {AngularEditorConfig} from '@kolkov/angular-editor';
import * as moment from 'moment';
import {Router} from '@angular/router';
import {SystemType} from 'src/app/model/enums/system.type';
import Quill from 'quill';
import BlotFormatter from 'quill-blot-formatter';
import {ConfirmationDialogComponent, InputSearchComponent} from 'src/app/components/fields';
import {PresetValue} from 'src/app/model/preset-value';
import {SearchableItem} from 'src/app/model/interface/searchable.item';

export enum PreviewSize {
  MEDIUM = 'medium',
  LARGE = 'large'
}

Quill.register('modules/blotFormatter', BlotFormatter);

@Component({
  selector: 'site-visit-manual-modal',
  templateUrl: './site-visit-manual.modal.html',
  styleUrls: ['./site-visit-manual.modal.scss']
})
export class SiteVisitManualModal implements OnInit, OnDestroy {
  @ViewChild('recipientField', {static: true}) recipientField: InputSearchComponent;
  @ViewChild('DragHandle', {static: true}) DragHandle: ElementRef;

  subject: Subject<boolean>;
  subscription: any;

  currentReport: Report;
  private reportSub: any;

  isLoading = false;
  htmlSubject = 'Site Visit Scheduled';
  recipients: any[] = [];
  cc: string[] = [];
  bcc: string[] = [];
  userTypes: PresetValue[] = [];
  private isSettingRecipients = false;

  confirmRef: BsModalRef;
  editorStyle = {'height': '400px'};

  // Draggable modal properties
  size: PreviewSize = PreviewSize.MEDIUM;
  hostElement: any;
  PreviewSize = PreviewSize; // Make enum available in template

  quillModules = {
    blotFormatter: {},
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{'list': 'ordered'}, {'list': 'bullet'}],
      [{'indent': '-1'}, {'indent': '+1'}],
      [{'header': [1, 2, 3, 4, 5, 6, false]}],
      [{'color': []}, {'background': []}],
      [{'align': []}],
      ['clean']
    ]
  };
  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: '25rem',
    minHeight: '5rem',
    placeholder: 'Enter text here...',
    translate: 'no'
  };

  htmlContent = '';
  siteVisitDate: Date = new Date(); // Default to today
  user: User;
  currentMode: SystemType;
  modeSub: any;
  disclaimer: string = `<div dir="ltr" style="text-size-adjust: auto;">
                <span style="caret-color: rgb(0, 0, 0); color: rgb(0, 0, 0); font-family: arial, helvetica, sans-serif; font-size: 12.8px; text-size-adjust: auto;">
                <span style="text-size-adjust: auto; color: rgb(102, 102, 102); font-family: &quot;courier new&quot;, monospace; font-size: x-small;">
                The information transmitted herewith is sensitive information intended only for use to the individual or entity to which it is addressed. &nbsp;</span>
                <span style="text-size-adjust: auto; color: rgb(102, 102, 102); font-family: &quot;courier new&quot;, monospace; font-size: x-small;">
                If the reader of this message is not the intended recipient, you are hereby notified that any review, retransmission, dissemination, distribution,
                copying or other use of, or taking of any action in reliance upon, this information is strictly prohibited.&nbsp; If you have received this communication in error,
                please contact the sender and delete the material from your computer.</span></span>
            </div>`;

  constructor(
    public bsModalRef: BsModalRef,
    private reportService: ReportService,
    private reportProvider: ReportProvider,
    private stageService: StageService,
    private userService: UserService,
    private modeService: ModeService,
    private router: Router,
    private modalService: BsModalService,
    private logService: LogService,
    public userProvider: ClientUserProvider,
    private calculationService: CalculationService
  ) {
  }

  ngOnInit(): void {
    // Fix for when user clicks `ESC` instead of clicking, no Event was firing
    this.subscription = this.modalService.onHide.subscribe((reason: string) => {
      if (reason === 'esc') {
        if (!this.isLoading)
          this.closePreview();
      }
    });

    this.reportSub = this.reportService.report$.subscribe(currentReport => {
      if (currentReport) {
        this.currentReport = currentReport;
        if (this.currentReport != null)
          if (this.currentReport.ID) {
            this.htmlSubject = this.currentReport.primaryEntity() + ' - Site Visit Scheduled';
            this.setRecipient();
          }
      }
    });
    this.modeSub = this.modeService.mode$.subscribe(mode => this.currentMode = mode);
    this.user = this.userService.getCurrentUser();

    this.loadDefaultEmailContent();
    this.loadRecipients();
    this.loadUserTypes();
  }

  ngOnDestroy(): void {
    if (this.subscription) this.subscription.unsubscribe();
    if (this.reportSub) this.reportSub.unsubscribe();
    if (this.modeSub) this.modeSub.unsubscribe();
  }

  loadDefaultEmailContent(): void {
    // Use the default email content as specified
    this.htmlContent = this.getDefaultEmailContent();
  }

  getDefaultEmailContent(): string {
    // Get recipient name for greeting
    const recipient = this.getSiteVisitRecipient();
    const recipientName = recipient?.NAME?.split(' ')[0] || '';
    const greeting = recipientName ? `Hello ${recipientName},` : 'Hello,';

    const companyName = this.currentReport.ENTITIES?.[0]?.NAME ?? 'Corporate';

    // Use selected site visit date or current date as inspection date
    const inspectionDate = moment(this.siteVisitDate).format('MMM D, YYYY');

    // Calculate completion date using TURN_FROM and TURN_TO from report with business days
    const turnFrom = this.currentReport.TURN_FROM || 0;
    const turnTo = this.currentReport.TURN_TO || 0;

    // If both are null/0, use default 7 days
    const daysToAdd = (turnFrom === 0 && turnTo === 0) ? 7 : turnTo;

    // Use business day calculation (excludes weekends and holidays)
    const estFrom = this.calculationService.getDueDateRegular(this.siteVisitDate, turnFrom);
    const estTo = this.calculationService.getDueDateRegular(this.siteVisitDate, daysToAdd);

    // Format completion date range
    const estText = turnFrom === turnTo
      ? moment(estFrom).format('MMM D, YYYY')
      : moment(estFrom).format('MMM D') + ' - ' + moment(estTo).format('MMM D, YYYY');

    return `<p>${greeting}</p><br/>
            <p>We wanted to update you on the equipment appraisal of ${companyName}.</p>
            <p>The site inspection is currently scheduled for <strong>${inspectionDate}</strong>.
            Barring any unforeseen setbacks, we should have the appraisal completed and sent to you by <strong>${estText}</strong>.</p><br/>
            <p>Should you have any questions please feel free to reach out.</p><br/>`;
  }

  /**
   * Update email content when site visit date changes
   */
  onSiteVisitDateChange(): void {
    this.htmlContent = this.getDefaultEmailContent();
  }

  loadRecipients(): void {
    if (this.currentReport.CLIENT_USERS) {
      for (const cl of this.currentReport.CLIENT_USERS) {
        if (cl.CLIENT_USER && cl.CLIENT_USER.EMAIL) {
          this.recipients.push({
            ID: cl.CLIENT_USER.ID,
            NAME: cl.CLIENT_USER.getName(),
            EMAIL: cl.CLIENT_USER.EMAIL,
            TYPE: cl.TYPE
          });
        }
      }
    }
  }

  loadUserTypes(): void {
    this.userTypes = [
      PresetValue.create('Client Users', 'CLIENT_USER'),
      PresetValue.create('All Users', 'ALL_USER')
    ];
  }

  onConfirm(): void {
    this.isLoading = true;

    if (this.recipients.length === 0) {
      this.logService.error('Please add at least one recipient.');
      this.isLoading = false;
      return;
    }

    // Create signature like engagement template
    let defaultSig = `<div style="text-align:left;font-weight:bold;" dir="ltr">${this.user.getName()}</div>`;
    defaultSig += '<div style="text-align:left;margin-bottom:3px;" dir="ltr">Reliant Business Valuation</div>';
    defaultSig += '<img src="https://www.reliantvalue.com/wp-content/uploads/2017/07/Updated-small.jpg" width="130px"/><br/>';
    defaultSig += '<p style="text-align:left;font-family:Helvetica,Arial,sans-serif;font-size:12px;margin-bottom:0;margin-top:3px;color:#5F5F5F;line-height:135%;">';
    defaultSig += '<div>Your Most Reputable Source for SBA Values&reg; </div>';
    defaultSig += '<div>1246 S. River Road, Ste 101 | Cranbury, NJ 08512 </div>';
    defaultSig += '<div>Office: (908) 888-6030  | Fax: (908) 888-6040 </div>';
    defaultSig += '<div><a href="www.reliantvalue.com">www.reliantvalue.com</a></div></p>';
    defaultSig += '<br/>';

    let signature = this.user.SIGNATURE ? this.user.SIGNATURE + '<br/>' : defaultSig;
    let content = this.htmlContent + signature + this.disclaimer;

    // Encode the email content like engagement template
    const encodedContent = btoa(content);

    // Deduplicate recipients by email to prevent duplicates
    const uniqueRecipients = this.recipients.filter((recipient, index, self) =>
      index === self.findIndex(r => r.EMAIL === recipient.EMAIL)
    );

    const body = {
      subject: this.htmlSubject,
      email: encodedContent,
      recipients: uniqueRecipients,
      cc: this.cc,
      bcc: this.bcc,
    };

    this.reportProvider.siteVisitManual(this.currentReport.ID, body, this.currentMode)
      .subscribe(data => {
        this.isLoading = false;
        if (data.success) {
          const siteVisitResult = ReportSiteVisitResult.parse(data.result);

          this.reportService.addStageHistory(siteVisitResult.HISTORY_STAGE);
          this.currentReport.STAGE = this.stageService.getStageById(siteVisitResult.HISTORY_STAGE.SID);
          this.currentReport.ASSIGNEE = siteVisitResult.HISTORY_STAGE.ASSIGNEE;
          this.currentReport.STATE = siteVisitResult.HISTORY_STAGE.STATE;
          this.currentReport.dateSiteVisitEmail = moment(siteVisitResult.DATE_SITE_VISIT_EMAIL).format('MMM DD, YYYY');

          this.showSuccessDialog();
        } else {
          this.logService.error(JSON.stringify(data, null, 2));
        }
      }, error => {
        this.isLoading = false;
        this.logService.error(error);
      });
  }

  closeModal(): void {
    this.subject.next(false);
    this.subject.complete();
  }

  closePreview(): void {
    this.closeModal();
  }

  showSuccessDialog(): void {
    this.confirmRef = this.modalService.show(ConfirmationDialogComponent, Helpers.getModalConfig());
    this.confirmRef.content.title = 'Success';
    this.confirmRef.content.text = 'Site Visit email sent successfully!';
    this.confirmRef.content.hideCancel = true;
    this.confirmRef.content.actionButton = 'Ok';
    this.confirmRef.content.actionButtonClass = 'success';

    const sub = new Subject<boolean>();
    this.confirmRef.content.subject = sub;
    sub.subscribe(value => {
      if (value) {
        this.subject.next(true);
        this.subject.complete();
        this.bsModalRef.hide();
      }
    });
  }

  recipientChanged(recs: SearchableItem[]): void {

    // Skip if we're programmatically setting recipients to prevent duplication
    if (this.isSettingRecipients) {
      return;
    }

    this.recipients = [];

    if(recs != null){
      this.recipients = recs.map(r =>({ NAME: r.getValue().NAME, EMAIL: r.getValue().EMAIL }));
    }
  }

  ccChanged(recs: SearchableItem[]): void {
    this.cc = [];
    if (recs != null) {
      this.cc = recs
        .map(r => (<ClientUser>r.getValue()).EMAIL);
    }
  }

  bccChanged(recs: SearchableItem[]): void {
    this.bcc = [];

    if (recs != null) {
      this.bcc = recs
        .map(r => (<ClientUser>r.getValue()).EMAIL);
    }
  }


  resetTemplate(): void {
    this.loadDefaultEmailContent();
  }

  onContentChanged(event: any): void {
    this.htmlContent = event.html;
  }

  expandArea(): void {
    this.size = this.size === PreviewSize.MEDIUM ? PreviewSize.LARGE : PreviewSize.MEDIUM;
  }

  getSiteVisitRecipient(): ClientUser {
    if (this.currentReport.CLIENT_USERS) {
      // First, try to find users with EMAIL_ENGAGE
      for (const user of this.currentReport.CLIENT_USERS) {
        if (user.EMAIL_ENGAGE)
          return user.CLIENT_USER;
      }

      // If no EMAIL_ENGAGE user found, show any PROCESSOR user for display purposes
      for (const user of this.currentReport.CLIENT_USERS) {
        if (user.TYPE === ReportClientType.PROCESSOR && user.CLIENT_USER)
          return user.CLIENT_USER;
      }
    }
    return null;
  }

  private setRecipient(): void {
    this.isSettingRecipients = true;
    this.recipientField.arrValue = [];
    this.recipients = [];

    for (const rcu of this.currentReport.CLIENT_USERS) {
      if (rcu.TYPE === ReportClientType.LSP) {
        this.recipientField.arrValue.push(rcu.CLIENT_USER);

        this.recipients.push({
          EMAIL: rcu.CLIENT_USER.EMAIL ? rcu.CLIENT_USER.EMAIL : "",
          NAME: rcu.CLIENT_USER.NAME ? rcu.CLIENT_USER.NAME : rcu.CLIENT_USER.EMAIL ? rcu.CLIENT_USER.EMAIL : ""
        });

      } else {
        if (rcu.CLIENT_USER != null && (rcu.EMAIL_ENGAGE === true || rcu.EMAIL_CC)) {
          this.recipientField.arrValue.push(rcu.CLIENT_USER);

           this.recipients.push({
            EMAIL: rcu.CLIENT_USER.EMAIL ? rcu.CLIENT_USER.EMAIL : "",
            NAME: rcu.CLIENT_USER.NAME ? rcu.CLIENT_USER.NAME : rcu.CLIENT_USER.EMAIL ? rcu.CLIENT_USER.EMAIL : ""
          });
        }
      }
    }

    // Reset the flag after the input field has been updated
    setTimeout(() => {
      this.isSettingRecipients = false;
    }, 500);
  }

}
